import { ReactNode } from 'react';

import { parseApiError } from '@cast/api-client';
import {
  Button,
  ButtonProps,
  HexIcon,
  Icons,
  useDrawerInstance,
  useDrawerOverlay,
  OverlayContent,
  Stack,
} from '@cast/design-system';
import { WorkloadScalingPolicy } from '@cast/types';

import { useAssignWorkloadsToPolicyMutation } from 'hooks/mutations/workload-optimization/useAssignWorkloadsToPolicyMutation';

type SubmittedSuccessfullyOverlayProps = {
  title: string;
  name: string;
};

const SubmittedSuccessfullyOverlay = ({
  title,
  name,
}: SubmittedSuccessfullyOverlayProps) => {
  return (
    <OverlayContent
      icon={<HexIcon variant="success" />}
      name={name}
      title={title}
      testId="successfully-submitted-overlay"
    />
  );
};

type FailedToSaveOverlayProps = {
  title: string;
  error?: ReactNode;
  tryAgainButtonProps?: ButtonProps;
  name?: string;
};

const FailedToSaveOverlay = ({
  title,
  error,
  tryAgainButtonProps,
  name,
}: FailedToSaveOverlayProps) => {
  const { destroyOverlay } = useDrawerOverlay();
  return (
    <OverlayContent
      icon={<HexIcon variant="error" icon={<Icons.Warning />} />}
      name={name}
      title={title}
      body={
        error || (
          <>
            Something went wrong.
            <br />
            Please try again.
          </>
        )
      }
      actions={
        <Button
          variant="text"
          size="lg"
          startIcon={<Icons.ArrowClockwise />}
          onClick={destroyOverlay}
          testId="try-again-btn"
          {...tryAgainButtonProps}
        >
          Try again
        </Button>
      }
      testId="failed-to-save-overlay"
    />
  );
};

type RetryAssignmentOverlayProps = {
  policy: WorkloadScalingPolicy;
  workloadIds: string[];
  error?: string;
};

const RetryAssignmentOverlay = ({
  policy,
  workloadIds,
  error,
}: RetryAssignmentOverlayProps) => {
  const { showOverlay } = useDrawerOverlay();

  const { mutate, isPending } = useAssignWorkloadsToPolicyMutation({
    onSuccess: () => {
      showOverlay({
        content: (
          <SubmittedSuccessfullyOverlay
            name={policy.name}
            title="Workloads assigned to the scaling policy"
          />
        ),
        duration: 1000,
        closeOnDestroy: true,
      });
    },
  });

  return (
    <FailedToSaveOverlay
      title="Failed to assign workloads to policy"
      name={policy.name}
      error={error}
      tryAgainButtonProps={{
        onClick: () =>
          mutate({
            clusterId: policy.clusterId,
            policyId: policy.id,
            body: { workloadIds },
          }),
        loading: isPending,
      }}
    />
  );
};

type PolicyAssignmentConfirmationOverlayProps = {
  policy: WorkloadScalingPolicy;
  workloadIds: string[];
  title?: string;
  body?: string;
  onCancel?: () => void;
};

export const PolicyAssignmentConfirmationOverlay = ({
  policy,
  workloadIds,
  title,
  body,
  onCancel,
}: PolicyAssignmentConfirmationOverlayProps) => {
  const { close } = useDrawerInstance();
  const { showOverlay } = useDrawerOverlay();
  const { mutate, isPending } = useAssignWorkloadsToPolicyMutation({
    onSuccess: () => {
      showOverlay({
        content: (
          <SubmittedSuccessfullyOverlay
            name={policy.name}
            title="Workloads assigned to the scaling policy"
          />
        ),
        duration: 1000,
        closeOnDestroy: true,
      });
    },
    onError: (error) => {
      showOverlay({
        content: (
          <RetryAssignmentOverlay
            policy={policy}
            workloadIds={workloadIds}
            error={parseApiError(error)}
          />
        ),
      });
    },
  });

  return (
    <OverlayContent
      icon={<HexIcon variant="check" icon={<Icons.Question />} />}
      name={policy.name}
      title={title}
      body={body}
      actions={
        <Stack direction="row" gap={16}>
          <Button
            variant="secondary"
            onClick={onCancel || (() => close())}
            disabled={isPending}
            testId="cancel-btn"
          >
            Do not assign
          </Button>
          <Button
            onClick={() => {
              mutate({
                clusterId: policy.clusterId,
                policyId: policy.id,
                body: { workloadIds },
              });
            }}
            loading={isPending}
            testId="save-btn"
          >
            Assign
          </Button>
        </Stack>
      }
      testId="save-confirmation-overlay"
    />
  );
};
